import React, { useEffect, useState } from 'react';
import { Input, InputNumber } from 'rsuite';
import { Button, Modal, useLastPresent } from '@nsi-advisory-services/components';

// Why a correction was made.
//
// Opens as soon as an amount is entered in the sheet, and again from the note
// icon beside an existing one. A correction is money moved by hand and the
// first thing an audit asks is why, so the reason is prompted for at the moment
// the amount is set rather than left to be filled in later.
//
// The amount is editable here too: somebody who opened this from the note icon
// to reword a reason will sooner or later want to adjust the figure with it,
// and sending them back to the cell for that would be busywork.
//
// One instance lives in PayrollMatrix rather than one per cell - a modal per
// employee would mean a roster's worth of them mounted for a dialog only ever
// opened once at a time. It serves both hand-entered rows - the payroll
// correction (hours) and the reimbursement adjustment (dollars) - picked by
// `target.kind`, since they differ only in what is being entered.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.
const KINDS = {
  correction: {
    title: 'Payroll correction',
    label: 'Hours',
    postfix: 'hours',
    noteKey: 'correctionNote',
    placeholder: 'What is this adjustment for?',
  },
  reimbursement: {
    title: 'Reimbursement adjustment',
    label: 'Amount',
    prefix: '$',
    noteKey: 'reimbursementAdjustmentNote',
    placeholder: 'What is this for? e.g. Lunch at the October all-hands',
  },
};

export default function PayrollCorrectionModal({ open, target, onClose, onSave, saving }) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const targetShown = useLastPresent(target); // keeps the dialog steady while it fades out
  const kind = KINDS[targetShown?.kind] || KINDS.correction;

  // Seed from whichever employee the sheet just opened this for. Keyed on the
  // employee so reopening on somebody else does not inherit the last one's
  // draft.
  useEffect(() => {
    if (!open || !target) return;
    setAmount(target.amount ?? 0);
    setReason(target.employee?.[(KINDS[target.kind] || KINDS.correction).noteKey] || '');
  }, [open, target]);

  const numeric = amount === '' || amount == null ? 0 : Number(amount);
  const valid = Number.isFinite(numeric);

  const handleSave = () => {
    if (!valid) return;
    onSave({
      kind: target.kind || 'correction',
      employeeId: target.employee.employeeId,
      amount: numeric,
      note: String(reason || '').trim() || null,
    });
  };

  return (
    <Modal open={open} onClose={onClose} size="xs">
      <Modal.Header>
        <Modal.Title>{kind.title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="payroll-correction-modal__who">
          {targetShown?.employee?.name}
          <span className="payroll-correction-modal__period">
            {targetShown?.period ? ` · ${targetShown.period}` : ''}
          </span>
        </div>

        <div className="bdc-modal__label">{kind.label}</div>
        <InputNumber
          value={amount}
          onChange={setAmount}
          onFocus={(event) => event.target.select()}
          prefix={kind.prefix}
          postfix={kind.postfix}
          step={1}
          style={{ width: 180, marginBottom: 16 }}
        />

        <div className="bdc-modal__label">Reason</div>
        <Input
          as="textarea"
          rows={4}
          value={reason}
          onChange={setReason}
          placeholder={kind.placeholder}
        />
        <div className="payroll-correction-modal__hint">
          Recorded against this pay period and included in the Excel export.
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} loading={saving} disabled={!valid}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
